import * as React from 'react'
import {useEffect, useState} from 'react'
import {Button, Col, DatePicker, Form, Radio, Row, Table, Typography} from 'antd';
import {addCommas, beautifyReportData, DateOption, roundBySlice, useWindowSize, WindowSize} from '../../../../util/Util';
import type { Dayjs } from 'dayjs';
import moment from 'dayjs';
import {
    DspReportKeyColumnDescription, DspReportValueColumnDescription,
    KeyFilterClause
} from '../../../../api/api';
import reportApi from '../../reports/ReportApi';
import {ColumnsType} from 'antd/lib/table';
import DspBarChart from './DspBarChart';
import DspPieChart from './DspPieChart';
import DspDoughnutChart from './DspDoughnutChart';

const {Text} = Typography;
const {RangePicker} = DatePicker;

const FormItem = Form.Item
const dateFormat = 'YYYY-MM-DD'
const DspKeyColumns = DspReportKeyColumnDescription.ColumnEnum
const DspValueColumns = DspReportValueColumnDescription.ColumnEnum
let chartData: DspData[] = [];

export interface DspData {
    name: string;
    spendings: string;
    bid: string;
    win: string;
    impression: string;
    view: string;
    click: string;
    vastStart: string;
    vastComplete: string;
}

export const DspDashboard = () => {
    const windowSize: WindowSize = useWindowSize();

    interface DataType {
        key: React.ReactNode;
        publisher: string;
        impressionsCount: string;
        winsCount: string;
        spendings: string;
        bids: string;
        children?: DataType[];
    }

    const [datePeriod, setDatePeriod] = useState<any>(DateOption.Month)
    const [dates, setDates] = useState<[Dayjs, Dayjs]>([moment(moment().subtract(1, 'months'), dateFormat), moment(moment(), dateFormat)])
    const [form] = Form.useForm()

    const [loading, setLoading] = useState(false)
    const [selectedRow, setSelectedRow] = useState()
    const [spendingChart, setSpendingChart] = useState(true)
    const [eventsChart, setEventsChart] = useState(false)
    const [hideDiagram, setHideDiagram] = useState(false)
    const [tableData, setTableData] = useState<DataType[]>([])
    const [colSpan, setColSpan] = useState(window.innerWidth < 1850 ? 12 : 8);
    const columns: ColumnsType<DataType> = [
        {
            title: 'Publisher',
            dataIndex: 'publisher',
            width: '25%',
            key: 'publisher',
        },
        {
            title: 'Spendings',
            dataIndex: 'spendings',
            width: '21%',
            key: 'spendings',
        },
        {
            title: 'Bids',
            dataIndex: 'bids',
            width: '18%',
            key: 'bids',
        },
        {
            title: 'Wins',
            dataIndex: 'winsCount',
            width: '18%',
            key: 'winsCount',
        },
        {
            title: 'Imp',
            dataIndex: 'impressionsCount',
            key: 'impressionsCount',
            width: '18%',
        }
    ]

    function prepareAndSetData(rowsData?: string[][]) {
        let tData: DataType[] = []

        rowsData?.forEach((row, idx) => {
            if (row[0]) {
                tData.push({
                    key: idx,
                    publisher: row[0],
                    spendings: row[1],
                    bids: row[2],
                    winsCount: row[3],
                    impressionsCount: row[4]
                });
            }
        });

        setTableData(tData)
    }

    const loadData = async () => {
        setLoading(true)
        let dateCol: DspReportKeyColumnDescription
        dateCol = {
            column: DspKeyColumns.Date,
            where: {
                type: KeyFilterClause.TypeEnum.Between,
                value: dates?.map(d => d.format(dateFormat))
            }
        }
        dateCol.onlyFilter = true
        const keyCols: DspReportKeyColumnDescription[] = []
        keyCols.push({column: DspKeyColumns.Publisher})
        keyCols.push(dateCol)
        const valueCols: DspReportValueColumnDescription[] = []
        valueCols.push({column: DspValueColumns.SpendGross})
        valueCols.push({column: DspValueColumns.Bids})
        valueCols.push({column: DspValueColumns.Wins})
        valueCols.push({column: DspValueColumns.Impressions})
        valueCols.push({column: DspValueColumns.View})
        valueCols.push({column: DspValueColumns.Click})
        valueCols.push({column: DspValueColumns.VastStart})
        valueCols.push({column: DspValueColumns.VastComplete})

        const uuid = await reportApi.createDsp({
            keyColumns: keyCols,
            valueColumns: valueCols
        }).then(resp => resp.data)
        if (!uuid) {
            return
        }
        let interval = setInterval(async () => {

            try {
                const response = await reportApi.get(uuid ?? '');
                clearInterval(interval);
                const blob = await response.blob();
                const text = await blob.text();
                if (text.length < 1) {
                    return;
                }
                const rows = text.split('\n');
                if (rows.length < 2) {
                    return;
                }
                let header = rows[0].split(',');
                rows.shift();
                rows.pop();
                let rowsData = rows.map(row => row.split(','));

                chartData = []
                rowsData.map(d => chartData.push({name: d[0], spendings: roundBySlice(d[1]), bid: d[2], win: d[3], impression: d[4], view: d[5], click: d[6], vastStart: d[7], vastComplete: d[8]}));
                beautifyReportData(header, rowsData);
                prepareAndSetData(rowsData);
                setLoading(false);
            } catch (response) {
                if (typeof response === 'object') {
                    if (response.status !== 425) {
                        setLoading(false);
                        clearInterval(interval);
                    }
                }
            }
        }, 500)
    }

    const initialLoad = () => {
        // form.setFieldsValue({datesPeriod: DateOption.Month})
        loadData()
    }

    useEffect(() => {
        initialLoad()
    }, [])

    useEffect(() => {
        if (windowSize.width && windowSize.width < 1850) {
            setHideDiagram(true);
            setColSpan(12)
        } else {
            setHideDiagram(false)
            setColSpan(8)
        }
    }, [windowSize])

    const handleDatePeriod = (e) => {
        setDatePeriod(e.target.value)
        setDates([moment(moment().subtract(1, e.target.value), dateFormat), moment(moment(), dateFormat)])
    }

    const handleDates = (e) => {
        setDatePeriod(undefined)
        setDates(e)
    }

    const rowSelect = (e) => {
        setSelectedRow(e ? e.publisher : undefined)
    }

    function selectChart(e) {
        setSpendingChart(false)
        setEventsChart(false)

        if (e.target.value === 'spending') {
            setSpendingChart(true)
        } else {
            setEventsChart(true)
        }
    }

    return (
        <div>
            <Row wrap={false}>
                <Col span={colSpan} hidden={hideDiagram && !spendingChart}>
                    <DspPieChart style={{
                        height: '415px'
                    }}
                              pubDat={chartData}
                    />
                    <Col>
                        <DspDoughnutChart style={{
                            height: '415px'
                        }}
                                       pubDat={chartData}
                        />
                    </Col>
                </Col>
                <Col span={colSpan} hidden={hideDiagram && !eventsChart}>
                    <DspBarChart style={{
                        height: '850px'
                    }}
                              data={chartData}
                             publisher={selectedRow}
                    />
                </Col>
                <Col span={colSpan}>
                    <Form
                        style={{
                            maxWidth: '1000px',
                            alignContent: 'center'
                        }}
                        onFinish={loadData}
                        form={form}
                        labelCol={{span: 1}}
                        wrapperCol={{span: 21}}
                    >
                        <FormItem style={{margin: '0px 0px -10px 10px'}}>
                            <Radio.Group id={'radioButtonsWithoutLines'} value={datePeriod} onChange={handleDatePeriod}>
                                {Object.keys(DateOption).map(option => <Radio.Button
                                    style={{border: '0px'}}
                                    key={option}
                                    value={option}>{option}</Radio.Button>)}
                            </Radio.Group>
                        </FormItem>

                        <FormItem
                            rules={[
                                {
                                    required: true,
                                    message: 'Please choose dates'
                                }
                            ]}
                        >
                            <RangePicker value={dates} onChange={handleDates}
                                         style={{width: '280', margin: '0 0 0 10px'}} format="YYYY-MM-DD"/>
                            <Button style={{
                                backgroundColor: '#657dcb', margin: '10px 10px 0 10px'
                            }} type="primary" htmlType="submit" loading={loading}>Apply</Button>
                        </FormItem>
                    </Form>
                    <Radio.Group defaultValue="spending" size="small" style={{ margin: '0px 0px 5px 10px' }} onChange={selectChart}>
                        <Radio.Button value="spending"><i className="topbar__dropdownMenuIcon icmn-stats-bars"/>Spending</Radio.Button>
                        <Radio.Button value="events"><i className="topbar__dropdownMenuIcon icmn-stats-bars"/>Events</Radio.Button>
                    </Radio.Group>
                    <Table
                        size={'middle'}
                        loading={loading}
                        id={'dashboardTable'}
                        scroll={{y: 600}}
                        bordered={true}
                        pagination={false}
                        columns={columns}
                        onRow={(record, rowIndex) => {
                            return {
                                onClick: () => {
                                    rowSelect(record)
                                },
                            };
                        }}
                        dataSource={tableData}
                        summary={pageData => {
                            let totalSpendings = 0;
                            let totalBids = 0;
                            let totalWins = 0;
                            let totalImpressions = 0;

                            pageData.forEach(({spendings, bids, winsCount, impressionsCount}) => {
                                totalSpendings += Number.parseFloat(spendings.replace(/[$\s,]/g, ''));
                                totalBids += Number.parseFloat(bids.replace(',', ''));
                                totalWins += Number.parseFloat(winsCount.replace(',', ''));
                                totalImpressions += Number.parseFloat(impressionsCount.replace(',', ''));
                            });

                            return (
                                <>
                                    <Table.Summary.Row style={{backgroundColor: 'lightgrey'}}>
                                        <Table.Summary.Cell index={0}>
                                            <a style={{display: 'flex'}}
                                             onClick={() => rowSelect(undefined)}>Total</a></Table.Summary.Cell>
                                        <Table.Summary.Cell index={1}>
                                            <Text>{'$ ' + addCommas(totalSpendings.toString())}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2}>
                                            <Text>{addCommas(totalBids.toString())}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2}>
                                            <Text>{addCommas(totalWins.toString())}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={3}>
                                            <Text>{addCommas(totalImpressions.toString())}</Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </>
                            );
                        }}
                    />
                </Col>
            </Row>
        </div>
    )
}
